import { Service, signal } from '@angular/core';

export type ToastKind = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

/** Cap the visible queue so a burst of events can't grow the list (or timer map) unbounded. */
const MAX_TOASTS = 4;

/** Transient notifications for socket/service events. Auto-dismiss; tap to close early. */
@Service()
export class ToastService {
  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  show(message: string, kind: ToastKind = 'info', durationMs = 4000): void {
    const text = message?.trim();
    if (!text) return;

    const id = ++this.nextId;
    const next = [...this._toasts(), { id, message: text, kind }];
    const overflow = next.length - MAX_TOASTS;
    if (overflow > 0) {
      for (const dropped of next.slice(0, overflow)) this.clearTimer(dropped.id);
      next.splice(0, overflow);
    }
    this._toasts.set(next);

    if (typeof setTimeout !== 'undefined' && durationMs > 0) {
      this.timers.set(id, setTimeout(() => this.dismiss(id), durationMs));
    }
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
